import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { MapPin, Star } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-cabanas.jpg";

const properties = [
  {
    name: "Son's Guadalupe",
    address: "7308 River Road, New Braunfels, TX 78132",
    type: "Private cabanas, in-water picnic tables, AC bell tent glamping",
    bestFor: "Families who want the float to end at their own private cabana",
    pick: true,
    cta: { label: "Book Son's Guadalupe", href: "/book-now/sons-guadalupe" },
  },
  {
    name: "River Road Sky Cabins & Treehouses",
    address: "River Road, New Braunfels, TX",
    type: "Elevated treehouse-style cabins",
    bestFor: "Couples wanting a unique elevated stay",
  },
  {
    name: "Camp Huaco Springs",
    address: "River Road, New Braunfels, TX",
    type: "RV park with riverside tent & cabin sites",
    bestFor: "RV travelers and tent campers",
  },
  {
    name: "Lazy L&L Campground",
    address: "River Road, New Braunfels, TX",
    type: "Campground with cabin and tent sites",
    bestFor: "Group camping with shuttle access",
  },
  {
    name: "Mountain Breeze Camp",
    address: "River Road, New Braunfels, TX",
    type: "Tent and cabin camping",
    bestFor: "Budget-friendly tent camping",
  },
  {
    name: "Camp Fimfo Texas Hill Country",
    address: "River Road, New Braunfels, TX",
    type: "Resort-style cabins, RV sites, amenities",
    bestFor: "Resort-style overnight stays with kids' activities",
  },
];

const RiverRoadCabinsGuide = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Cabins on River Road, New Braunfels — A Local Guide"
        description="Honest local guide to cabins on River Road in New Braunfels: Son's Guadalupe, Sky Cabins, Camp Huaco, Lazy L&L, Mountain Breeze, Camp Fimfo & more."
        canonical="/river-road-new-braunfels-cabins"
        ogImage="/og/property-map.jpg"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Cabins on River Road, New Braunfels",
          itemListElement: properties.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: { "@type": "LodgingBusiness", name: p.name, address: p.address, description: p.type },
          })),
        }}
      />
      <Header />
      <main className="pt-24">
        <section className="relative h-[50vh] min-h-[380px]">
          <img src={heroImage} alt="Aerial view of riverside cabins along River Road in New Braunfels, TX" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <p className="text-coral-orange font-medium mb-3">Local guide</p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">Cabins on River Road, New Braunfels</h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  An honest, locally-written guide to the cabins, treehouses, and glamping spots along River Road on the Guadalupe River.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">What "River Road" actually means</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              River Road runs from FM 306 down along the Guadalupe River through New Braunfels, passing the Second Crossing, Third Crossing, and dozens of riverside outfitters, campgrounds, and cabin operators. It's the most concentrated stretch of family-friendly river lodging in the Texas Hill Country.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Below is every notable cabin operator on River Road, what they're best known for, and where they're located. We're a local — Son's Guadalupe sits at 7308 River Road, half a mile past the Second Crossing — so our pick is honest about who fits which traveler.
            </p>
          </div>
        </section>

        <section className="section-padding bg-cream">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 text-center">
              Every River Road cabin operator
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {properties.map((p) => (
                <div key={p.name} className={`bg-white rounded-2xl p-6 shadow-soft ${p.pick ? "ring-2 ring-coral-orange" : ""}`}>
                  {p.pick && (
                    <div className="inline-flex items-center gap-1 bg-coral-orange text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                      <Star className="w-3 h-3 fill-white" />
                      Local pick
                    </div>
                  )}
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{p.name}</h3>
                  <p className="text-sm text-teal mb-3 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.address}</p>
                  <p className="text-charcoal/80 text-sm mb-2"><strong>Type:</strong> {p.type}</p>
                  <p className="text-charcoal/80 text-sm mb-4"><strong>Best for:</strong> {p.bestFor}</p>
                  {p.cta && (
                    <Link to={p.cta.href}>
                      <Button variant="default" size="sm">{p.cta.label}</Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-teal text-white">
          <div className="container-narrow mx-auto px-4 md:px-8 text-center max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Want the float to end at your cabana?</h2>
            <p className="text-white/85 text-lg mb-6">That's the one thing only Son's Guadalupe does on River Road.</p>
            <Link to="/book-now/sons-guadalupe"><Button variant="hero" size="xl">Book Son's Guadalupe</Button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default RiverRoadCabinsGuide;
